import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IActivity,
    IQuestion,
    ISubmitTesting,
    IStartTesting,
    IStartTestingResponse,
    ISubmitQuestionTesting,
    IResultTesting,
    ISurveyResponse,
    ISurveyUser,
} from '@/features/landing/interface';
import {
    IHistoryTestResponse as IAssessmentResponse,
    ICreateTestingResponse,
} from '@/services/testingService';
import { IGetListAssessmentParams } from '@/features/student/Assessments/interface';
import { ICommonGetListResponse, IPagination } from '@/common/interface';
import { LIMIT_FOR_PAGINATION } from '@/features/student/Assessments/constants';
import { DEFAULT_FIRST_PAGE } from '@/common/constants';

interface TestingState {
    loadingState: {
        isLoading: boolean;
        isLoadingSubmitQuestion: boolean;
        getResult: boolean;
        getAssessmentList: boolean;
        getAssessmentDetails: boolean;
        createTestingLesson: boolean;
    };
    error?: string | null;
    survey?: ISurveyResponse;
    testing?: IStartTestingResponse;
    questions: IQuestion[];
    activities: IActivity[];
    estimated_duration_minutes: number;
    activity?: IActivity;
    resultTesting?: IResultTesting;
    assessment_id?: string;
    token?: string;
    session?: string;
    currentQuestion: number;
    assessmentList: IAssessmentResponse[];
    assessmentDetails?: IAssessmentResponse;
    assessmentData?: ICreateTestingResponse;
    query: IGetListAssessmentParams;
    pagination: IPagination;
}

const DEFAULT_ASSESSMENT_LIST_QUERY: IGetListAssessmentParams = {
    page: DEFAULT_FIRST_PAGE,
    page_size: LIMIT_FOR_PAGINATION,
};
const DEFAULT_ASSESSMENT_PAGINATION: IPagination = {
    count: 0,
    page: DEFAULT_FIRST_PAGE,
    page_size: LIMIT_FOR_PAGINATION,
    total_pages: 0,
};

const initialState: TestingState = {
    loadingState: {
        isLoading: false,
        isLoadingSubmitQuestion: false,
        getResult: false,
        getAssessmentList: false,
        getAssessmentDetails: false,
        createTestingLesson: false,
    },
    error: null,
    survey: undefined,
    testing: undefined,
    activities: [],
    estimated_duration_minutes: 0,
    activity: undefined,
    questions: [],
    resultTesting: undefined,
    assessment_id: '',
    token: undefined,
    session: undefined,
    currentQuestion: 0,
    assessmentList: [],
    assessmentDetails: undefined,
    assessmentData: undefined,
    query: DEFAULT_ASSESSMENT_LIST_QUERY,
    pagination: DEFAULT_ASSESSMENT_PAGINATION,
};

const testingSlice = createSlice({
    name: 'testing',
    initialState,
    reducers: {
        setQuery: (state, action: PayloadAction<IGetListAssessmentParams>) => {
            state.query = action.payload;
        },
        setPagination: (state, action: PayloadAction<IPagination>) => {
            state.pagination = action.payload;
        },
        fetchCreateSurveyRequest: (state: TestingState, action: PayloadAction<ISurveyUser>) => {
            state.loadingState.isLoading = true;
            state.error = null;
        },
        fetchCreateSurveySuccess: (state: TestingState, action: PayloadAction<ISurveyResponse>) => {
            state.loadingState.isLoading = false;
            state.survey = action.payload;
            state.error = null;
        },
        fetchCreateSurveyFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.isLoading = false;
            state.error = action.payload;
        },
        fetchStartTestingRequest: (state: TestingState, action: PayloadAction<IStartTesting>) => {
            state.loadingState.isLoading = true;
            state.error = null;
        },
        fetchStartTestingSuccess: (state: TestingState, action: PayloadAction<IStartTestingResponse>) => {
            state.loadingState.isLoading = false;
            state.testing = action.payload;
            state.questions = action.payload.result.activities as IQuestion[];
            state.assessment_id = action.payload.result.assessment_id;
            state.token = action.payload.token;
            state.session = action.payload.session;
            state.estimated_duration_minutes = action.payload.result.estimated_duration_minutes;
            state.error = null;
        },
        fetchStartTestingFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.isLoading = false;
            state.error = action.payload;
        },
        fetchSubmitQuestionTestingRequest: (state: TestingState, action: PayloadAction<ISubmitQuestionTesting>) => {
            state.loadingState.isLoadingSubmitQuestion = true;
            state.error = null;
        },
        fetchSubmitQuestionTestingSuccess: (state: TestingState, action: PayloadAction<any>) => {
            state.loadingState.isLoadingSubmitQuestion = false;
            state.error = null;
            state.questions?.push(action.payload.next_activity);
        },
        fetchSubmitQuestionTestingFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.isLoadingSubmitQuestion = false;
            state.error = action.payload;
        },
        fetchSubmitTestingRequest: (state: TestingState, action: PayloadAction<ISubmitTesting>) => {
            state.loadingState.isLoading = true;
            state.error = null;
        },
        fetchSubmitTestingSuccess: (state: TestingState) => {
            state.loadingState.isLoading = false;
            state.error = null;
        },
        fetchSubmitTestingFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.isLoading = false;
            state.error = action.payload;
        },

        fetchResultTestingRequest: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.getResult = true;
            state.error = null;
        },
        fetchResultTestingSuccess: (state: TestingState, action: PayloadAction<IResultTesting>) => {
            state.loadingState.getResult = false;
            state.resultTesting = action.payload;
            state.error = null;
        },
        fetchResultTestingFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.getResult = false;
            state.error = action.payload;
        },

        // New actions for history list
        fetchHistoryListRequest: (state: TestingState, action: PayloadAction<IGetListAssessmentParams>) => {
            state.loadingState.getAssessmentList = true;
            state.error = null;
        },
        fetchHistoryListSuccess: (
            state: TestingState,
            action: PayloadAction<ICommonGetListResponse<IAssessmentResponse>>,
        ) => {
            state.loadingState.getAssessmentList = false;
            state.assessmentList = action.payload.results;
            state.pagination = {
                page: action.payload.page || DEFAULT_FIRST_PAGE,
                page_size: action.payload.page_size || LIMIT_FOR_PAGINATION,
                count: action.payload.count || 0,
                total_pages: Math.ceil(action.payload.count / action.payload.page_size) || 0,
            };
            state.error = null;
        },
        fetchHistoryListFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.getAssessmentList = false;
            state.error = action.payload;
        },

        // New actions for history details
        fetchHistoryDetailsRequest: (state: TestingState, action: PayloadAction<string | number>) => {
            state.loadingState.getAssessmentDetails = true;
            state.error = null;
        },
        fetchHistoryDetailsSuccess: (
            state: TestingState,
            action: PayloadAction<{ message: string; data: IAssessmentResponse }>,
        ) => {
            state.loadingState.getAssessmentDetails = false;
            state.assessmentDetails = action.payload.data;
            state.error = null;
        },
        fetchHistoryDetailsFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.getAssessmentDetails = false;
            state.error = action.payload;
        },

        // New actions for create testing lesson
        fetchCreateTestingLessonRequest: (state: TestingState, action: PayloadAction<'full_test' | 'quick_test'>) => {
            state.loadingState.createTestingLesson = true;
            state.error = null;
        },
        fetchCreateTestingLessonSuccess: (state: TestingState, action: PayloadAction<ICreateTestingResponse>) => {
            state.loadingState.createTestingLesson = false;
            state.assessmentData = action.payload;
            state.error = null;
        },
        fetchCreateTestingLessonFailure: (state: TestingState, action: PayloadAction<string>) => {
            state.loadingState.createTestingLesson = false;
            state.error = action.payload;
        },

        addActivities: (state: TestingState, action: PayloadAction<IActivity>) => {
            if (state.activity) return;
            state.activity = action.payload;

            const isExistingActivity = state.activities.some((item) => item.activity_id === action.payload.activity_id);
            if (isExistingActivity) return;
            state.activities.push(action.payload);
            state.activity = action.payload;
        },
        setCurrentQuestion: (state: TestingState, action: PayloadAction<number>) => {
            state.currentQuestion = action.payload;
        },
        setTestingData: (state: TestingState, action: PayloadAction<ICreateTestingResponse>) => {
            state.assessmentData = action.payload;
        },
        clearQuestionResults: (state: TestingState) => {
            state.activities = [];
        },
        clearResult: (state: TestingState) => {
            state.activity = undefined;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearSurvey: (state: TestingState) => {
            state.currentQuestion = 0;
        },
        clearHistoryDetails: (state: TestingState) => {
            state.assessmentDetails = undefined;
        },
        clearCreateTestingLessonResponse: (state: TestingState) => {
            state.assessmentData = undefined;
        },
    },
});

export const {
    setQuery,
    setPagination,
    fetchCreateSurveyRequest,
    fetchCreateSurveySuccess,
    fetchCreateSurveyFailure,
    fetchStartTestingRequest,
    fetchStartTestingSuccess,
    fetchStartTestingFailure,
    fetchSubmitTestingRequest,
    fetchSubmitTestingSuccess,
    fetchSubmitTestingFailure,
    fetchSubmitQuestionTestingRequest,
    fetchSubmitQuestionTestingSuccess,
    fetchSubmitQuestionTestingFailure,
    fetchResultTestingRequest,
    fetchResultTestingSuccess,
    fetchResultTestingFailure,
    fetchHistoryListRequest,
    fetchHistoryListSuccess,
    fetchHistoryListFailure,
    fetchHistoryDetailsRequest,
    fetchHistoryDetailsSuccess,
    fetchHistoryDetailsFailure,
    fetchCreateTestingLessonRequest,
    fetchCreateTestingLessonSuccess,
    fetchCreateTestingLessonFailure,
    setCurrentQuestion,
    setTestingData,
    addActivities,
    clearQuestionResults,
    clearResult,
    clearError,
    clearSurvey,
    clearHistoryDetails,
    clearCreateTestingLessonResponse,
} = testingSlice.actions;

export default testingSlice.reducer;
